import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API } from "@/App";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!token) {
      toast.error("Missing reset token — request a fresh link.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });
      const raw = await res.text();
      let data = {};
      try { data = raw ? JSON.parse(raw) : {}; } catch { data = {}; }
      if (!res.ok) throw new Error(
        (typeof data.detail === "string" && data.detail) || "Reset failed"
      );
      toast.success("Password updated. Please sign in.");
      navigate("/login");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 islamic-pattern">
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-gold-400 font-outfit mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        <div className="glass-card rounded-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="/hifz-logo.png"
              alt="Hifz"
              className="w-20 h-20 rounded-full mx-auto mb-4 shadow-lg"
            />
            <h1 className="font-cinzel text-2xl font-bold text-slate-100">Choose a new password</h1>
            <p className="text-slate-400 font-outfit text-sm mt-2">
              Your reset link is valid for 60 minutes.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="password" className="text-slate-300 font-outfit">
                New password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="password"
                  data-testid="new-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 text-slate-100 placeholder:text-slate-600"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="confirm" className="text-slate-300 font-outfit">
                Confirm password
              </Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  id="confirm"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter the password"
                  required
                  className="pl-10 bg-islamic-blue-950/50 border-white/10 text-slate-100 placeholder:text-slate-600"
                />
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading || !token}
              data-testid="submit-reset-btn"
              className="w-full btn-gold rounded-full py-6 font-outfit"
            >
              {loading ? "Updating…" : "Update password"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
